import { Link } from "react-router-dom";
import {
  CheckCircle2, FlaskConical, ClipboardList, PackageCheck, Microscope,
  FileText, Droplets, ArrowRight, MapPin,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FAQ } from "@/components/FAQ";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { SchemaJsonLd, serviceSchema, faqSchema, localBusinessSchema } from "@/lib/seo";

const BOOK = "/book-water-test";

const FAQS = [
  { q: "How often should bore water be tested?",
    a: "Testing frequency depends on how the water is used, previous results and whether anything has changed in the bore or surrounding environment. If bore water is relied upon as part of a private drinking-water supply, periodic testing can help identify changes that may otherwise go unnoticed." },
  { q: "My bore water looks clear. Do I still need to test it?",
    a: "Clear water can still contain dissolved minerals or microbiological indicators that are not visible. Appearance alone does not provide a complete picture of water quality." },
  { q: "Can you test bore water for E. coli?",
    a: "Yes, microbiological testing can be included depending on the selected testing package." },
  { q: "Can bore water be tested before I install filtration?",
    a: "Yes. In many cases this is the preferred approach because the results can help identify what type of treatment may be appropriate." },
  { q: "Can I send a bore-water sample from outside the Sunshine Coast?",
    a: "Yes, AWARE supports testing pathways for customers outside the immediate local area." },
  { q: "What happens if something is outside the guideline range?",
    a: "Your report will identify relevant results and provide practical interpretation. Depending on the result, further investigation, treatment or retesting may be recommended." },
];

const WHO_FOR = [
  "rely on bore water for household use",
  "are considering drinking bore water",
  "have recently purchased a rural or acreage property",
  "notice staining, taste, odour or discolouration",
  "are planning a filtration or treatment system",
  "want to understand salinity, pH or mineral levels",
  "have not tested the bore recently",
  "have experienced a change in water quality",
];

const PARAMS = [
  "E. coli", "pH", "electrical conductivity", "total dissolved solids",
  "selected minerals and metals",
  "indicators relevant to bore-water suitability and treatment planning",
];

const STEPS = [
  [ClipboardList, "Book your test", "Choose your bore-water test and complete your booking online."],
  [Droplets, "Collect your sample", "Follow the supplied sampling instructions so the sample is collected correctly."],
  [PackageCheck, "Send or return the sample", "Depending on your location and testing pathway, your sample can be delivered or returned for analysis."],
  [Microscope, "We analyse the results", "The selected parameters are tested and reviewed."],
  [FileText, "Receive your report", "You receive clear results with practical interpretation so you can understand what was found and what the next step may be."],
];

const ISSUES = [
  ["High salinity", "May affect taste and suitability for some household uses."],
  ["Low or high pH", "Can influence corrosion, treatment performance and the behaviour of other water-quality parameters."],
  ["Iron or manganese", "Can contribute to staining, colour or deposits."],
  ["Microbiological contamination", "Testing can help identify indicators such as E. coli where included in the selected package."],
  ["High dissolved mineral content", "Bore water can contain substantial dissolved minerals even when it looks perfectly clear."],
];

const CTA = ({ to = BOOK, children, testid, secondary = false }) => (
  <Link to={to} data-testid={testid}
    className={secondary
      ? "inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-cyan-600 text-cyan-700 font-bold hover:bg-cyan-50 transition-colors"
      : "inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold transition-colors"}>
    {children} <ArrowRight className="w-4 h-4" />
  </Link>
);

export default function BoreWaterTestingLanding() {
  const url = `${SEO_DEFAULTS.baseUrl}/bore-water-testing`;
  useSEO({
    title: "Bore Water Testing Australia | AWARE Water Testing",
    description:
      "Test your bore water with AWARE Water Testing. Practical bore water analysis for private water supplies, with clear reporting and easy booking.",
    url,
  });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SchemaJsonLd data={localBusinessSchema()} id="jsonld-localbusiness" />
      <SchemaJsonLd id="jsonld-service"
        data={serviceSchema("Bore Water Testing",
          "Practical bore water testing for homes, rural properties, businesses and private water supplies across Australia.", "Australia")} />
      <SchemaJsonLd id="jsonld-faq" data={faqSchema(FAQS)} />
      <SchemaJsonLd id="jsonld-breadcrumb" data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SEO_DEFAULTS.baseUrl },
          { "@type": "ListItem", position: 2, name: "Bore Water Testing", item: url },
        ],
      }} />
      <Header />

      {/* HERO */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Breadcrumbs items={[{ name: "Bore Water Testing", url: "/bore-water-testing" }]} />
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold" data-testid="bore-landing-h1">
            Bore Water Testing
          </h1>
          <p className="mt-3 text-lg text-cyan-300 font-semibold">
            Understand what&rsquo;s in your bore water before you rely on it.
          </p>
          <div className="mt-5 max-w-2xl space-y-3 text-slate-300 leading-relaxed">
            <p>AWARE Water Testing provides practical bore water testing for homes, rural properties, businesses and private water supplies.</p>
            <p>Whether you use bore water for drinking, household use, irrigation or as part of a mixed water supply, testing helps you understand the water you&rsquo;re working with and whether further treatment or investigation may be worthwhile.</p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <CTA testid="bore-hero-book-cta">Book a Bore Water Test</CTA>
            <a href="#what-we-test" data-testid="bore-hero-secondary-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-500 text-slate-200 font-bold hover:border-cyan-400 hover:text-cyan-300 transition-colors">
              See What We Test
            </a>
          </div>
          <p className="mt-5 text-xs text-slate-400">Clear reporting. Practical interpretation. Australia-wide options available.</p>
        </div>
      </section>

      {/* WHY TEST */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide text-cyan-700">Bore water can change from property to property</h2>
        <div className="mt-4 max-w-3xl space-y-3 text-slate-700 leading-relaxed">
          <p>Two bores only a few kilometres apart can produce very different water.</p>
          <p>Bore water quality can be affected by local geology, dissolved minerals, salinity, acidity or alkalinity, metals, bacteria, bore depth, surrounding land use and seasonal conditions.</p>
          <p>Testing gives you a clearer picture of your own water rather than relying on assumptions based on appearance, taste or location.</p>
        </div>
        <div className="mt-6"><CTA testid="bore-why-cta">Book My Bore Water Test</CTA></div>
      </section>

      {/* WHO FOR */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide text-slate-900">Bore water testing may be useful if you:</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {WHO_FOR.map((x) => (
              <li key={x} className="flex items-start gap-2.5 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 shrink-0" /> {x}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHAT CAN BE TESTED */}
      <section id="what-we-test" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide text-cyan-700">What we can look at</h2>
        <p className="mt-3 max-w-3xl text-slate-700">Your testing package can include a range of microbiological and water-quality indicators depending on the test selected. Typical areas may include:</p>
        <ul className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
          {PARAMS.map((x) => (
            <li key={x} className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800">
              <FlaskConical className="w-4 h-4 text-cyan-600 mt-0.5 shrink-0" /> {x}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-slate-500">The exact parameters depend on the package selected.</p>
        <div className="mt-5"><a href="#bore-360" data-testid="bore-params-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-cyan-600 text-cyan-700 font-bold hover:bg-cyan-50 transition-colors">View Bore Water Testing Options <ArrowRight className="w-4 h-4" /></a></div>
      </section>

      {/* RECOMMENDED PACKAGE */}
      <section id="bore-360" className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-cyan-400">Recommended package</div>
            <h2 className="mt-2 text-base md:text-lg font-display font-extrabold uppercase tracking-wide">AWARE Bore 360</h2>
            <p className="mt-1 text-cyan-300 font-semibold">A more complete picture of your bore water.</p>
            <div className="mt-4 space-y-3 text-slate-300 leading-relaxed">
              <p>AWARE Bore 360 is designed for properties relying on bore water and provides a broader assessment than a basic screening test.</p>
              <p>It is suitable when you want to better understand the characteristics of the water before deciding on filtration, treatment or ongoing use.</p>
            </div>
            <p className="mt-4 text-sm text-slate-400">Want the full package detail? See the <Link to="/aware-bore-360" data-testid="bore-360-detail-link" className="text-cyan-300 underline hover:text-cyan-200">AWARE Bore 360 package page</Link>.</p>
          </div>
          <div className="rounded-2xl bg-white text-slate-900 p-6 sm:p-8 shadow-xl" data-testid="bore-360-card">
            <div className="font-display font-extrabold text-xl">AWARE Bore 360</div>
            <div className="mt-3 text-xs font-bold uppercase tracking-wider text-slate-500">Best for</div>
            <ul className="mt-2 space-y-1.5">
              {["bore-fed homes", "acreage properties", "private water supplies", "treatment planning", "water-quality troubleshooting"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-cyan-600 shrink-0" /> {x}</li>
              ))}
            </ul>
            <div className="mt-6"><CTA testid="bore-360-book-cta">Book AWARE Bore 360</CTA></div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide text-cyan-700">Bore water testing made simple</h2>
        <ol className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {STEPS.map(([Icon, t, d], i) => (
            <li key={t} className="rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 text-cyan-700"><Icon className="w-5 h-5" /><span className="text-xs font-extrabold">STEP {i + 1}</span></div>
              <div className="mt-2 font-bold text-slate-900 text-sm">{t}</div>
              <p className="mt-1 text-xs text-slate-600 leading-relaxed">{d}</p>
            </li>
          ))}
        </ol>
        <div className="mt-8"><CTA testid="bore-how-cta">Book a Bore Water Test</CTA></div>
      </section>

      {/* CLEAR RESULTS */}
      <section className="bg-cyan-50 border-y border-cyan-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide text-slate-900">Clear results you can actually understand</h2>
          <p className="mt-3 max-w-3xl text-slate-700">Water-test results can be confusing when they are presented without context. AWARE reports are designed to help you understand:</p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-3 max-w-3xl">
            {["what was detected", "whether a result is outside the relevant guideline range", "what the result may mean for your private water supply", "whether further investigation, treatment or retesting may be appropriate"].map((x) => (
              <li key={x} className="flex items-start gap-2.5 text-slate-800"><CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 shrink-0" /> {x}</li>
            ))}
          </ul>
          <p className="mt-5 max-w-3xl text-sm text-slate-600">Where relevant, we can also help identify the type of treatment approach that may warrant further investigation.</p>
        </div>
      </section>

      {/* COMMON ISSUES */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide text-cyan-700">What can show up in bore water?</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ISSUES.map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="font-bold text-slate-900">{t}</div>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-600">Wondering whether bore water can be relied on for drinking? Read our guide: <Link to="/blog/is-bore-water-safe-to-drink" data-testid="bore-blog-link" className="text-cyan-700 font-semibold underline">Is bore water safe to drink?</Link></p>
        <p className="mt-3 text-sm text-slate-600">On rainwater rather than bore, or running both? You can also <Link to="/aware-tank-360" data-testid="bore-link-tank-360" className="text-cyan-700 font-semibold underline">test your tank water</Link> with the comprehensive AWARE Tank 360 package.</p>
      </section>

      {/* TEST BEFORE FILTRATION */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide text-slate-900">Test first. Treat second.</h2>
          <div className="mt-4 max-w-3xl space-y-3 text-slate-700 leading-relaxed">
            <p>If you are considering a filtration or treatment system for bore water, testing first can help avoid guessing. Different water problems require different treatment approaches.</p>
            <p>A filter designed for sediment does not necessarily address dissolved minerals. A UV system does not remove salinity or metals. A treatment system should be selected based on the actual water-quality problem rather than appearance alone.</p>
          </div>
          <div className="mt-6"><CTA testid="bore-treat-cta">Test My Bore Water First</CTA></div>
        </div>
      </section>

      {/* AUSTRALIA-WIDE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-start gap-3 max-w-3xl">
          <MapPin className="w-6 h-6 text-cyan-600 mt-1 shrink-0" />
          <div>
            <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide text-cyan-700">Bore water testing wherever you are</h2>
            <p className="mt-3 text-slate-700 leading-relaxed">AWARE Water Testing supports private water supplies across Australia through our testing and sample-return pathways. If you are unsure which test is appropriate, start with the booking process and choose the option that best matches your water source.</p>
            <div className="mt-5"><CTA testid="bore-auswide-cta">Book Bore Water Testing</CTA></div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
          <div className="mt-6"><FAQ qas={FAQS} title="Bore water testing FAQs" /></div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-base md:text-lg font-display font-extrabold uppercase tracking-wide">Know your bore water before you treat it</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">Whether you are relying on bore water every day or simply want to understand what is coming out of your bore, testing gives you a much better starting point. Book your bore water test with AWARE Water Testing.</p>
          <div className="mt-7 flex justify-center"><CTA testid="bore-final-cta">Book a Bore Water Test</CTA></div>
          <p className="mt-4 text-xs text-slate-400">Clear testing. Clear reporting. Practical next steps.</p>
        </div>
      </section>

      <Footer />

      {/* MOBILE STICKY CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-2.5 flex items-center justify-between gap-3"
        data-testid="bore-sticky-cta" style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}>
        <span className="text-sm font-bold text-slate-900">Bore Water Testing</span>
        <Link to={BOOK} data-testid="bore-sticky-book-btn"
          className="px-5 py-2 rounded-full bg-cyan-600 text-white text-sm font-bold">Book Now</Link>
      </div>
    </div>
  );
}
